import { Box, Paper, Typography, useTheme } from '@mui/material';
import { ArrowForward, ArrowDownward } from '@mui/icons-material';

export interface FlowNode {
    label: string;
    icon?: React.ReactNode;
    /** MUI palette colour used for the node accent. */
    color?: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary';
}

interface FlowDiagramProps {
    title?: string;
    nodes: FlowNode[];
    /** Optional caption shown under the flow (e.g. an alternative outcome). */
    caption?: string;
}

/**
 * Generic, theme-aware step/flow illustration: a wrapping row of accented
 * nodes connected by arrows. Reused by several help topics to depict simple
 * sequential processes. Presentational only.
 */
const FlowDiagram = ({ title, nodes, caption }: FlowDiagramProps) => {
    const theme = useTheme();

    return (
        <Box
            component="figure"
            aria-label={title}
            sx={{ m: 0, my: 2, p: 2, borderRadius: 2, bgcolor: 'action.hover' }}
        >
            {title && (
                <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1.5, fontWeight: 600 }}
                >
                    {title}
                </Typography>
            )}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'stretch',
                    gap: 1,
                }}
            >
                {nodes.map((node, index) => {
                    const color = node.color ?? 'primary';
                    const isLast = index === nodes.length - 1;
                    return (
                        <Box
                            key={`${node.label}-${index}`}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    px: 1.5,
                                    py: 1,
                                    minHeight: 44,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette[color].main}`,
                                    bgcolor: `${theme.palette[color].main}14`,
                                    color: `${color}.main`,
                                }}
                            >
                                {node.icon}
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: 'text.primary' }}
                                >
                                    {node.label}
                                </Typography>
                            </Paper>
                            {!isLast && (
                                <>
                                    <ArrowForward
                                        fontSize="small"
                                        sx={{
                                            color: 'text.disabled',
                                            display: { xs: 'none', sm: 'block' },
                                        }}
                                    />
                                    <ArrowDownward
                                        fontSize="small"
                                        sx={{
                                            color: 'text.disabled',
                                            display: { xs: 'block', sm: 'none' },
                                        }}
                                    />
                                </>
                            )}
                        </Box>
                    );
                })}
            </Box>
            {caption && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                    {caption}
                </Typography>
            )}
        </Box>
    );
};

export default FlowDiagram;
